// Extended clinical metric set (OPED-parity, honest).
//
// Timing metrics (cadence, step time, double support, stance %, gait
// variability) need NO calibration. Spatial metrics (speed km/h, step/stride
// length cm) require a height-based calibration; when absent they are omitted
// rather than guessed. Variability is a CV (scale-free) so it needs no
// calibration even though it concerns step length.

import { Joints, type PoseFrame, type Side } from "../pose/landmarks";
import type { EventResult, GaitEvent } from "./events";
import type { Calibration } from "./calibration";
import { mean, std } from "./signal";
import { symmetryIndex } from "./geometry";

export interface SidePair {
  L: number | null;
  R: number | null;
}

export interface ClinicalMetrics {
  stepCount: number;
  cadenceStepsPerMin: number | null;
  // Timing (no calibration)
  stepTimeS: SidePair;
  doubleSupportPct: SidePair;
  stanceTimePct: SidePair;
  gaitVariabilityPct: SidePair; // step-length CV ×100
  // Spatial (height-calibrated; null if no calibration)
  speedKmh: number | null;
  stepLengthCm: SidePair;
  strideLengthCm: SidePair;
  // Symmetry summaries
  stepLengthSymmetryIndex: number | null;
  stepTimeSymmetryIndex: number | null;
  spatialConfidence: Calibration["confidence"] | "none";
}

export interface ClinicalContext {
  aspect: number; // width / height
  calibration: Calibration | null;
}

export function computeClinicalMetrics(
  frames: PoseFrame[],
  events: EventResult,
  ctx: ClinicalContext,
): ClinicalMetrics {
  const { aspect, calibration } = ctx;
  const cmPerUnit = calibration?.cmPerUnit ?? null;

  const hs = events.events.filter((e) => e.type === "HS").sort((a, b) => a.tMs - b.tMs);
  const stepCount = hs.length;

  // --- Cadence ---
  const stepIntervals: number[] = [];
  for (let i = 1; i < hs.length; i++) stepIntervals.push(hs[i].tMs - hs[i - 1].tMs);
  const meanStepMs = stepIntervals.length ? mean(stepIntervals) : null;
  const cadence = meanStepMs && meanStepMs > 0 ? 60000 / meanStepMs : null;

  // --- Per-side step time: interval ending at this side's contact ---
  const stepTimeS: SidePair = { L: null, R: null };
  for (const side of ["L", "R"] as Side[]) {
    const intervals: number[] = [];
    for (let i = 1; i < hs.length; i++) {
      if (hs[i].side === side) intervals.push((hs[i].tMs - hs[i - 1].tMs) / 1000);
    }
    stepTimeS[side] = intervals.length ? mean(intervals) : null;
  }

  // --- Step lengths (iso units) per side; leading foot = contact's side ---
  const stepIso: Record<Side, number[]> = { L: [], R: [] };
  for (let i = 1; i < hs.length; i++) {
    const prev = frames[hs[i - 1].index];
    const cur = frames[hs[i].index];
    const dxIso =
      Math.abs(new Joints(cur).footIndex(hs[i].side).x -
        new Joints(prev).footIndex(hs[i - 1].side).x) * aspect;
    stepIso[hs[i].side].push(dxIso);
  }

  // --- Gait variability = step-length CV (scale-free) ---
  const gaitVariabilityPct: SidePair = {
    L: cv(stepIso.L),
    R: cv(stepIso.R),
  };

  // --- Stance % from cycles ---
  const stanceTimePct: SidePair = {
    L: sideStancePct(events, "L"),
    R: sideStancePct(events, "R"),
  };

  // --- Double support % per side ---
  const doubleSupportPct: SidePair = {
    L: doubleSupport(events.events, "L"),
    R: doubleSupport(events.events, "R"),
  };

  // --- Spatial (calibrated) ---
  let speedKmh: number | null = null;
  const stepLengthCm: SidePair = { L: null, R: null };
  const strideLengthCm: SidePair = { L: null, R: null };
  if (cmPerUnit) {
    for (const side of ["L", "R"] as Side[]) {
      stepLengthCm[side] = stepIso[side].length
        ? mean(stepIso[side]) * cmPerUnit
        : null;
      const strideIso = strideLengths(frames, events.events, side, aspect);
      strideLengthCm[side] = strideIso.length ? mean(strideIso) * cmPerUnit : null;
    }
    // Speed from pelvis horizontal displacement over the clip.
    const durS = frames.length > 1 ? (frames[frames.length - 1].tMs - frames[0].tMs) / 1000 : 0;
    if (durS > 0) {
      const dispIso = Math.abs(
        new Joints(frames[frames.length - 1]).pelvisCenter().x -
          new Joints(frames[0]).pelvisCenter().x,
      ) * aspect;
      const meters = (dispIso * cmPerUnit) / 100;
      speedKmh = (meters / durS) * 3.6;
    }
  }

  return {
    stepCount,
    cadenceStepsPerMin: cadence,
    stepTimeS,
    doubleSupportPct,
    stanceTimePct,
    gaitVariabilityPct,
    speedKmh,
    stepLengthCm,
    strideLengthCm,
    stepLengthSymmetryIndex: pairSI(stepLengthCm),
    stepTimeSymmetryIndex: pairSI(stepTimeS),
    spatialConfidence: calibration?.confidence ?? "none",
  };
}

function cv(xs: number[]): number | null {
  if (xs.length < 2) return null;
  const m = mean(xs);
  return m > 0 ? (std(xs) / m) * 100 : null;
}

function pairSI(p: SidePair): number | null {
  if (p.L === null || p.R === null) return null;
  return symmetryIndex(p.L, p.R);
}

function sideStancePct(events: EventResult, side: Side): number | null {
  const cs = events.cycles.filter((c) => c.side === side);
  const vals = cs.map((c) => c.stancePct).filter((v): v is number => v !== null);
  return vals.length ? mean(vals) : null;
}

/** Stance intervals [HS, next ipsilateral TO] for a side. */
function stanceIntervals(
  events: GaitEvent[],
  side: Side,
): Array<{ start: number; end: number }> {
  const out: Array<{ start: number; end: number }> = [];
  const hs = events.filter((e) => e.side === side && e.type === "HS");
  const to = events.filter((e) => e.side === side && e.type === "TO");
  for (const h of hs) {
    const t = to.find((e) => e.tMs > h.tMs);
    if (t) out.push({ start: h.tMs, end: t.tMs });
  }
  return out;
}

/** Double-support fraction (%) within each gait cycle of `side`. */
function doubleSupport(events: GaitEvent[], side: Side): number | null {
  const other: Side = side === "L" ? "R" : "L";
  const thisStance = stanceIntervals(events, side);
  const otherStance = stanceIntervals(events, other);
  if (thisStance.length === 0 || otherStance.length === 0) return null;

  const hs = events.filter((e) => e.side === side && e.type === "HS").sort((a, b) => a.tMs - b.tMs);
  const fractions: number[] = [];
  for (let i = 0; i < hs.length - 1; i++) {
    const start = hs[i].tMs;
    const end = hs[i + 1].tMs;
    const cycle = end - start;
    if (cycle <= 0) continue;
    // Within this cycle, time this foot is in stance AND other foot in stance.
    const myStanceInCycle = thisStance
      .map((s) => overlap(s, { start, end }))
      .reduce((a, b) => a.concat(b), [] as Array<{ start: number; end: number }>);
    let ds = 0;
    for (const ms of myStanceInCycle) {
      for (const os of otherStance) ds += overlapLen(ms, os);
    }
    fractions.push((ds / cycle) * 100);
  }
  return fractions.length ? mean(fractions) : null;
}

function overlap(
  a: { start: number; end: number },
  b: { start: number; end: number },
): Array<{ start: number; end: number }> {
  const s = Math.max(a.start, b.start);
  const e = Math.min(a.end, b.end);
  return e > s ? [{ start: s, end: e }] : [];
}
function overlapLen(
  a: { start: number; end: number },
  b: { start: number; end: number },
): number {
  return Math.max(0, Math.min(a.end, b.end) - Math.max(a.start, b.start));
}

/** Stride lengths (iso) = AP distance between consecutive same-foot contacts. */
function strideLengths(
  frames: PoseFrame[],
  events: GaitEvent[],
  side: Side,
  aspect: number,
): number[] {
  const hs = events.filter((e) => e.side === side && e.type === "HS").sort((a, b) => a.tMs - b.tMs);
  const out: number[] = [];
  for (let i = 1; i < hs.length; i++) {
    const prev = new Joints(frames[hs[i - 1].index]).footIndex(side).x;
    const cur = new Joints(frames[hs[i].index]).footIndex(side).x;
    out.push(Math.abs(cur - prev) * aspect);
  }
  return out;
}
